import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import { SprayCan } from "lucide-react";
import { signInWithGoogle } from "../lib/native";
import { useAuth } from "../hooks/useAuthStore";
import { takePendingAuthorization } from "../lib/oauthPending";

export default function LoginPage() {
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    // If an AI assistant (OAuth client) sent the user here to sign in, resume its consent screen
    const pendingAuthorizationId = takePendingAuthorization();
    if (pendingAuthorizationId) {
      history.push(`/oauth/consent?authorization_id=${encodeURIComponent(pendingAuthorizationId)}`);
      return;
    }
    // If there was a pending invite before login, send user back to complete it
    const pendingInviteCode = localStorage.getItem("pendingInviteCode");
    if (pendingInviteCode) {
      localStorage.removeItem("pendingInviteCode");
      history.push(`/invite?code=${pendingInviteCode}`);
      return;
    }
    history.push("/");
  }, [user, history]);

  const handleGoogleLogin = async () => {
    try {
      const { error } = await signInWithGoogle();

      if (error) {
        console.error("Login error:", error);
        alert(`Login error: ${error.message}`);
      }
    } catch (error) {
      console.error("Caught exception during login:", error);
      alert(
        `Login error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
          <div className="max-w-sm w-full">
            <div className="text-center mb-10">
              <div
                aria-hidden="true"
                className="w-20 h-20 rounded-[28px] bg-gradient-to-br from-sky-400 to-sky-600 shadow-glow-cyan flex items-center justify-center mx-auto mb-6"
              >
                <SprayCan className="w-10 h-10 text-white" strokeWidth={2.2} />
              </div>
              <h1 className="text-[32px] leading-tight font-extrabold tracking-tight text-foreground mb-2">
                Tidy
              </h1>
              <p className="text-muted-foreground text-base">
                Track your cleaning tasks and achievements
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-10" aria-hidden="true">
              {[
                { label: "Small steps", tone: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-200" },
                { label: "Streaks", tone: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200" },
                { label: "Teamwork", tone: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200" },
              ].map((chip) => (
                <span
                  key={chip.label}
                  className={`rounded-2xl px-2 py-2 text-center text-xs font-bold ${chip.tone}`}
                >
                  {chip.label}
                </span>
              ))}
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center min-h-[56px] px-5 rounded-full border border-border bg-card shadow-soft text-[15px] font-semibold text-foreground hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-all active:scale-[0.98]"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
